import React from "react";
import { Button, Modal, Dropdown, Tooltip } from "antd";
import { ExportOutlined, DownOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import styled, { createGlobalStyle } from "styled-components";
import { WHITE, GRAY_300, GRAY_500, GRAY_600, BLACK, PRIMARY_50, PRIMARY_500, GRAY_400 } from "../../constant/colors";
import { sizes, fontWeights } from "../../constant/styles";
import LoadingIcon from "./LoadingIcon";
import { showExportConfirmModal } from "../../utils/export";
import { FILE_TYPES, MAX_EXPORT_RECORDS } from "../../constant/export";

const ExportDropdownStyles = createGlobalStyle`
  .export-dropdown-menu {
    .ant-dropdown-menu {
      border-radius: 8px;
      padding: 0;
      width: 120px;
    }
    .ant-dropdown-menu-item {
      padding: 8px 12px;
      color: ${BLACK};
      font-size: 14px;
      font-weight: 400;
      user-select: none;
    }
    .ant-dropdown-menu-item:first-child {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }
    .ant-dropdown-menu-item:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
    .ant-dropdown-menu-item:hover:not(.ant-dropdown-menu-item-disabled) {
      background-color: ${PRIMARY_50};
    }
    .ant-dropdown-menu-item:hover:not(.ant-dropdown-menu-item-disabled) span {
      color: ${PRIMARY_500};
    }
    .ant-dropdown-menu-item:hover:not(.ant-dropdown-menu-item-disabled) span > .anticon {
      color: ${BLACK};
    }
    .ant-dropdown-menu-item:hover:not(.ant-dropdown-menu-item-disabled) .anticon-right {
      color: ${GRAY_500} !important;
    }
    .ant-dropdown-menu-item-disabled:hover {
      background-color: transparent !important;
      cursor: not-allowed !important;
    }
    .ant-dropdown-menu-item-disabled:hover span {
      color: ${GRAY_400} !important;
    }
    .ant-dropdown-menu-item[id*="export"]:hover:not(.ant-dropdown-menu-item-disabled) span > span svg {
      color: ${BLACK} !important;
    }
  }
`;

const StyledDropdownButton = styled(Button)`
  height: 32px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .anticon {
    font-size: ${sizes.large};
    font-weight: ${fontWeights.bold};
    width: 18px;
    height: 18px;
  }

  & > .anticon + span,
  & > span + .anticon {
    margin-left: 12px;
    color: ${GRAY_500};
  }

  &.loading-state > span {
    display: flex;
    align-items: center;
  }

  &:active {
    color: ${BLACK};
    border-color: ${GRAY_600};
  }

  &:focus {
    color: ${GRAY_600};
    border-color: ${GRAY_600};
    box-shadow: 0 0 0 2px rgba(228, 231, 236, 1) !important;
  }

  &:hover:not(:disabled) {
    color: ${BLACK} !important;
    border-color: ${GRAY_600} !important;
  }

  &:disabled,
  &[disabled] {
    cursor: not-allowed;

    &:not(.loading-state) {
      background: ${WHITE} !important;
      border-color: ${GRAY_300} !important;
      color: ${GRAY_300} !important;

      .anticon {
        color: ${GRAY_300} !important;
      }

      &:hover {
        background: ${WHITE} !important;
        border-color: ${GRAY_300} !important;
        color: ${GRAY_300} !important;
      }
    }

    &.loading-state {
      background: ${WHITE} !important;
      border-color: ${GRAY_300} !important;
      color: ${BLACK} !important;

      &:hover {
        background: ${WHITE} !important;
        border-color: ${GRAY_300} !important;
        color: ${BLACK} !important;
      }
    }
  }
`;

const ExportButton = ({ recordCount, onExport, isLoading, disabled, className, selectedView }) => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();

  // Handle edge cases: undefined, null, negative numbers
  const validRecordCount = typeof recordCount === "number" && recordCount >= 0 ? recordCount : 0;
  const isDisabledByRecordCount = validRecordCount > MAX_EXPORT_RECORDS;
  const isButtonDisabled = disabled || isDisabledByRecordCount || isLoading || validRecordCount === 0;

  const getTooltipText = () => {
    if (validRecordCount > MAX_EXPORT_RECORDS) {
      return t("export.record_limit_exceeded_tooltip", { limit: MAX_EXPORT_RECORDS.toLocaleString() });
    }
    return "";
  };

  const handleExportClick = (fileType) => {
    showExportConfirmModal(modal, {
      recordCount: validRecordCount,
      fileType,
      t,
      onConfirm: onExport
    });
  };

  const handleMenuClick = ({ key }) => {
    handleExportClick(key);
  };

  const menuItems = [
    {
      key: FILE_TYPES.CSV,
      id: `export-${selectedView}-csv-option`,
      label: <span data-pendo={`export-${selectedView}-csv-option`}>{t("export.csv_option")}</span>
    },
    {
      key: FILE_TYPES.XLSX,
      id: `export-${selectedView}-xlsx-option`,
      label: <span data-pendo={`export-${selectedView}-xlsx-option`}>{t("export.xlsx_option")}</span>
    }
  ];

  const buttonContent = (
    <>
      <ExportDropdownStyles />
      <Dropdown
        menu={{
          items: menuItems,
          onClick: handleMenuClick
        }}
        disabled={isButtonDisabled}
        trigger={["click"]}
        overlayClassName="export-dropdown-menu"
      >
        <StyledDropdownButton
          type="default"
          id={`export-${selectedView}-button`}
          data-pendo={`export-${selectedView}-button`}
          disabled={isButtonDisabled}
          className={`${className} ${isLoading ? "loading-state" : ""}`}
          aria-label={t("export.button_label")}
          aria-disabled={isButtonDisabled}
          aria-busy={isLoading}
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <>
              <LoadingIcon />
              <span style={{ marginLeft: "8px" }}>{t("export.loading_text")}</span>
            </>
          ) : (
            <>
              <ExportOutlined />
              <span style={{ marginLeft: "8px" }}>{t("export.button_text")}</span>
              <DownOutlined
                style={{ fontSize: "14px", width: "14px", height: "14px", marginLeft: "10px", color: GRAY_500 }}
              />
            </>
          )}
        </StyledDropdownButton>
      </Dropdown>
    </>
  );

  return (
    <>
      {isDisabledByRecordCount ? (
        <Tooltip
          title={getTooltipText()}
          placement="topRight"
          arrow={{ pointAtCenter: false }}
          overlayInnerStyle={{
            padding: "10px 10px",
            width: "330px",
            color: BLACK,
            backgroundColor: WHITE,
            borderRadius: 8,
            fontWeight: fontWeights.regular,
            fontSize: sizes.small
          }}
          overlayStyle={{ maxWidth: "450px", borderRadius: "8px" }}
          color={WHITE}
        >
          <span style={{ display: "inline-block" }}>{buttonContent}</span>
        </Tooltip>
      ) : (
        buttonContent
      )}
      {contextHolder}
    </>
  );
};

ExportButton.propTypes = {
  recordCount: PropTypes.number.isRequired,
  onExport: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  listType: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  selectedView: PropTypes.string
};

ExportButton.defaultProps = {
  isLoading: false,
  disabled: false,
  className: "",
  selectedView: ""
};

export default ExportButton;
